import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import type { ReactElement } from 'react';
import { Fragment, useEffect, useState } from 'react';

import { Box } from '@/components/Box';
import { Button } from '@/components/Button';
import { Container } from '@/components/Container';
import { Flex } from '@/components/Flex';
import { Loader } from '@/components/Loader';
import { LoginRequired } from '@/components/LoginRequired';
import { RegisterWatchModal } from '@/components/RegisterWatchModal';
import { ServerSuccesses } from '@/components/ServerSuccesses';
import { FixedPageDocument } from '@/graphql/contentful/api';
import type { ProductVariantWatchSkuFragment } from '@/graphql/saleor/api';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useMyWatches } from '@/hooks/useMyWatches';
import { useRouter } from '@/hooks/useRouter';
import {
  type ContentfulFixedPageCMSData,
  generateSectionKey,
  renderContentfulFixedPage,
} from '@/integrations/contentful';
import { AccountLayout } from '@/layouts/AccountLayout';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import * as MA from '@/messages/myAccount';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';
import {
  removeWatchMessageStateActions,
  useRemoveWatchMessageState,
} from '@/states/removeWatchMessageState';

import { MyWatchesList } from './components/MyWatchesList';
import { RegisterMyWatchBanner } from './components/RegisterMyWatchBanner';

import * as M from './messages';
import * as S from './MyWatchesView.styles';

type Product = ProductVariantWatchSkuFragment['product'];
export interface Watch extends Product {
  country: string;
  created_at: string | null;
  end_date: string;
  extension_end_date: string;
  extension_start_date: string;
  is_bind: boolean;
  is_extendable: boolean;
  is_extended: boolean;
  is_visible: boolean;
  order_id: string;
  product_name?: string;
  sku: string;
  sn: string;
  start_date: string;
  updated_at: string | null;
  warranty_code?: string;
}
export interface Eyewear extends Product {
  sku: string;
  sn: string;
}

export interface MyWatchesViewProps {
  cms?: ContentfulFixedPageCMSData;
}

export const MyWatchesView = ({ cms }: MyWatchesViewProps) => {
  const liveCms = useContentfulLiveUpdates(cms, {
    query: FixedPageDocument,
  });
  const [isOpen, setIsOpen] = useState(false);
  const { type } = useRemoveWatchMessageState();
  const { push } = useRouter();
  const [watches, setWatches] = useState<Watch[] | null>(null);
  const Provider = useRegionProvider();
  const {
    getWatchBySaleor,
    getWatchesList,
    loading: useMyWatchesLoading,
  } = useMyWatches();
  const { sendEvent } = useGoogleAnalytics();
  const [loading, setLoading] = useState(false);
  const [successes, setSuccesses] = useState(false);
  const [removeSuccess, setRemoveSuccess] = useState(false);
  const handleBuyWatch = () => {
    push(paths.watches.url());
  };
  useEffect(() => {
    (async () => {
      const res = await getWatchesList();
      if (res.data) {
        const watches = await getWatchBySaleor(res.data as Watch[], Provider);
        setWatches((watches.watches as Watch[]) ?? []);
        setLoading(watches.loading);
      } else {
        setLoading(false);
      }
    })();
  }, []);
  useEffect(() => {
    if (type) {
      setRemoveSuccess(type);
    }
    setTimeout(() => {
      removeWatchMessageStateActions.changeRemoveWatchMessageStateActions(
        false,
      );
      setRemoveSuccess(false);
    }, 3000);
  }, [type]);
  useEffect(() => {
    if (successes)
      setTimeout(() => {
        setSuccesses(false);
      }, 3000);
  }, [successes]);
  if (loading || useMyWatchesLoading) {
    return <Loader data-test-id='MyWatchesViewLoader' height={500} />;
  }
  return (
    <Box data-test-id='MyWatchesViewBox'>
      {liveCms?.topContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {loading
            ? null
            : renderContentfulFixedPage(
                data,
                undefined,
                liveCms.cmsExtraData,
                !!watches && watches?.length > 0,
                true,
              )}
        </Fragment>
      ))}
      <Container data-test-id='MyWatchesViewContainer' marginTop={8}>
        <RegisterWatchModal
          data-test-id='MyWatchesViewRegisterWatchModal'
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          setSuccesses={setSuccesses}
          setWatches={setWatches}
          watches={watches || []}
        />
        {successes && (
          <Flex
            data-test-id='MyWatchesViewSuccessesFlex'
            justifyContent='center'
          >
            <ServerSuccesses data-test-id='MyWatchesViewServerSuccesses'>
              {MA.YourWatchHaveBeenAdded}
            </ServerSuccesses>
          </Flex>
        )}
        {removeSuccess && (
          <Flex
            data-test-id='MyWatchesViewRemoveSuccessFlex'
            justifyContent='center'
          >
            <ServerSuccesses data-test-id='MyWatchesViewServerSuccesses'>
              {MA.YourWatchHaveBeenDeleted}
            </ServerSuccesses>
          </Flex>
        )}

        {watches && watches?.length > 0 && (
          <S.MyWatchesListWrapper>
            <MyWatchesList
              data-test-id='MyWatchesListView'
              onRegisterWatchClick={() => setIsOpen(true)}
              watches={watches}
            />
            <Flex data-test-id='MyWatchesViewFlex' justifyContent='center'>
              <Button
                data-test-id='MyWatchesViewButton'
                onClick={() => {
                  sendEvent({
                    event: EVENT_NAME.INTENTION_REGISTER_WATCH,
                  });
                  setIsOpen(true);
                }}
                variant='outlined'
              >
                {M.RegisterNewWatch}
              </Button>
            </Flex>
          </S.MyWatchesListWrapper>
        )}
        {watches && watches?.length === 0 && (
          <RegisterMyWatchBanner
            data-test-id='MyWatchesViewRegisterMyWatchBanner'
            onBuyWatchClick={handleBuyWatch}
            onRegisterWatchClick={() => {
              sendEvent({ event: EVENT_NAME.INTENTION_REGISTER_WATCH });
              setIsOpen(true);
            }}
          />
        )}
      </Container>
      {liveCms?.bottomContent?.map((data, i) => (
        <Fragment key={generateSectionKey(data, i)}>
          {loading
            ? null
            : renderContentfulFixedPage(
                data,
                undefined,
                liveCms.cmsExtraData,
                !!watches && watches?.length > 0,
                true,
              )}
        </Fragment>
      ))}
    </Box>
  );
};

MyWatchesView.getLayout = (page: ReactElement) => (
  <DraftModeContext.Provider value={page.props.draftMode}>
    <LoginRequired>
      <AccountLayout cmsData={page.props.cmsDefaultLayoutData}>
        {page}
      </AccountLayout>
    </LoginRequired>
  </DraftModeContext.Provider>
);
